import React, { useId } from 'react';
import { Link } from 'react-router-dom';
import { GanttChart, type GanttChartProps } from './GanttChart';
import { buildGanttItems, type GanttItem } from './ganttRows';
import { toDateInputValue } from '../utils/date';
import '../packages/ui/Table.css';

const HEALTH_LABEL: Record<string, string> = {
  on_track: 'On track', late: 'Late', done: 'Done', no_target: 'No target',
};

function slipText(slip?: number): string {
  if (slip === undefined) return '—';
  return slip > 0 ? `${slip}d late` : slip < 0 ? `${Math.abs(slip)}d ahead` : 'On track';
}

/**
 * The Gantt chart's accessible alternative: the same rows (buildGanttItems),
 * grouped the same way, as a semantic table. Every value the chart encodes
 * visually — dates, critical path, baseline, slip, milestone health — is text
 * here. Task names link to the task, like a bar click on the chart.
 */
export function GanttTable({ tasks, milestones, phases, criticalTaskIds, baselineByTask, slipByTask, baselineMilestones, milestoneHealth, phaseRollups }: GanttChartProps) {
  const captionId = useId();
  const items = buildGanttItems(tasks, milestones, phases, criticalTaskIds);
  const hasBaseline = !!(baselineByTask?.size || baselineMilestones?.size);
  const hasSlip = !!slipByTask?.size;
  const hasHealth = !!milestoneHealth?.size;
  const cols = 5 + (hasBaseline ? 1 : 0) + (hasSlip ? 1 : 0) + (hasHealth ? 1 : 0);

  // Split into row groups: each header starts a new <tbody>.
  const groups: { header?: Extract<GanttItem, { type: 'header' }>; rows: Extract<GanttItem, { type: 'row' }>['row'][] }[] = [];
  for (const item of items) {
    if (item.type === 'header') groups.push({ header: item, rows: [] });
    else {
      if (groups.length === 0) groups.push({ rows: [] });
      groups[groups.length - 1].rows.push(item.row);
    }
  }

  return (
    <div className="table-wrapper ui-table-scroll" role="region" tabIndex={0} aria-labelledby={captionId}>
      <table className="table ui-table ui-table--compact" data-testid="gantt-table">
        <caption id={captionId} className="ui-table-caption">Timeline as a table</caption>
        <thead>
          <tr>
            <th scope="col">Item</th>
            <th scope="col">Type</th>
            <th scope="col">Start</th>
            <th scope="col">Due / target</th>
            <th scope="col">Critical path</th>
            {hasBaseline && <th scope="col">Baseline</th>}
            {hasSlip && <th scope="col">Slip</th>}
            {hasHealth && <th scope="col">Milestone health</th>}
          </tr>
        </thead>
        {groups.map((g, gi) => {
          const rollup = g.header?.phaseId ? phaseRollups?.get(g.header.phaseId) : undefined;
          return (
            <tbody key={gi}>
              {g.header && (
                <tr className="gantt-table-group">
                  <th scope="rowgroup" colSpan={cols}>
                    {g.header.label}
                    {rollup && (
                      <span className="gantt-table-rollup">
                        {' '}· {rollup.start && rollup.end ? `${toDateInputValue(rollup.start)} – ${toDateInputValue(rollup.end)} · ` : ''}
                        {rollup.doneCount}/{rollup.taskCount} done
                      </span>
                    )}
                  </th>
                </tr>
              )}
              {g.rows.map(row => {
                const h = row.isMilestone ? milestoneHealth?.get(row.id) : undefined;
                const baseline = row.isMilestone
                  ? toDateInputValue(baselineMilestones?.get(row.id))
                  : (() => {
                      const b = baselineByTask?.get(row.id);
                      return b ? `${toDateInputValue(b.start) || '—'} → ${toDateInputValue(b.due) || '—'}` : '';
                    })();
                return (
                  <tr key={row.id} data-testid={`gantt-table-row-${row.id}`}>
                    <th scope="row">
                      {!row.isMilestone ? <Link to={`/tasks/${row.id}`}>{row.label}</Link> : row.label}
                    </th>
                    <td>{row.isMilestone ? 'Milestone' : 'Task'}</td>
                    <td>{row.isMilestone ? '—' : toDateInputValue(row.start) || '—'}</td>
                    <td>{toDateInputValue(row.due) || '—'}{h?.projected ? ` (projected ${toDateInputValue(h.projected)})` : ''}</td>
                    <td>{row.isMilestone ? '—' : row.critical ? 'Yes' : 'No'}</td>
                    {hasBaseline && <td>{baseline || '—'}</td>}
                    {hasSlip && <td>{row.isMilestone ? '—' : slipText(slipByTask?.get(row.id))}</td>}
                    {hasHealth && <td>{h ? `${HEALTH_LABEL[h.health] ?? h.health}${h.floatDays != null ? ` (${h.floatDays >= 0 ? '+' : ''}${h.floatDays}d float)` : ''}` : '—'}</td>}
                  </tr>
                );
              })}
            </tbody>
          );
        })}
      </table>
    </div>
  );
}

/**
 * Chart / Table view switch for a Gantt: the chart is pointer-only and not
 * readable by a screen reader, so the same data is one toggle away as a table.
 * With nothing dated there is no toggle — the chart's empty state says why.
 */
export function GanttWithTable(props: GanttChartProps) {
  const [mode, setMode] = React.useState<'chart' | 'table'>('chart');
  const hasRows = buildGanttItems(props.tasks, props.milestones).length > 0;
  if (!hasRows) return <GanttChart {...props} />;
  return (
    <div className="gantt-view">
      <div className="gantt-view-toggle" role="group" aria-label="Timeline display">
        <button type="button" className={`btn btn-sm ${mode === 'chart' ? 'btn-secondary' : 'btn-ghost'}`} aria-pressed={mode === 'chart'} onClick={() => setMode('chart')}>Chart view</button>
        <button type="button" className={`btn btn-sm ${mode === 'table' ? 'btn-secondary' : 'btn-ghost'}`} aria-pressed={mode === 'table'} onClick={() => setMode('table')}>Table view</button>
      </div>
      {mode === 'chart' ? <GanttChart {...props} /> : <GanttTable {...props} />}
    </div>
  );
}
